/**
 * Keeps IMA's ad UI laid out for the player's real size.
 *
 * IMA measures the player exactly once, in `adsManager.init()`, and never
 * re-reads it. Two things in this app resize the player after that point:
 * the MRC-pin layouts in `main.js` write `.player-wrap` width/height from
 * JS, and the runner changes the viewport mid-scenario (`resize` action).
 *
 * Without an explicit `adsManager.resize()` the ad chrome keeps the old
 * geometry, and the control bar drifts on top of the Skip Ad button. That is
 * not just cosmetic: Playwright clicks an element's centre and aborts when
 * something else receives the pointer event there, so a covered skip button
 * makes the scenario's Skip Ad action fail with no visible reason.
 */

/** Used only when the player cannot be measured yet; corrected on first sync. */
export const DEFAULT_AD_SIZE = { width: 640, height: 360 };

/**
 * Measure an element, or null when it has no layout yet.
 *
 * Returning null rather than a fallback keeps "not measurable yet" distinct
 * from "genuinely 640x360", so the caller can retry instead of baking a wrong
 * size into IMA.
 *
 * @param {{ clientWidth?: number, clientHeight?: number } | null | undefined} element
 * @returns {{ width: number, height: number } | null}
 */
export function readElementSize(element) {
  if (!element) return null;
  const width = Math.round(element.clientWidth || 0);
  const height = Math.round(element.clientHeight || 0);
  if (width <= 0 || height <= 0) return null;
  return { width, height };
}

/**
 * Watch `element` and push its size into IMA whenever it changes.
 *
 * @param {object} opts
 * @param {{ clientWidth?: number, clientHeight?: number } | null} opts.element
 *   The IMA render surface. Tracked by reference so a re-rendered layout is
 *   picked up without re-wiring.
 * @param {() => { resize?: Function } | null} opts.getAdsManager
 *   Read lazily: the manager only exists after ADS_MANAGER_LOADED.
 * @param {unknown} opts.viewMode - `google.ima.ViewMode` value.
 * @param {{ width: number, height: number } | null} [opts.initialSize]
 *   The size already handed to `adsManager.init()`, so a matching first sync
 *   is not re-sent to IMA.
 * @param {(element: unknown, onChange: () => void) => () => void} [opts.observe]
 *   Subscribe to size changes; returns an unsubscribe. Injected for tests.
 * @param {(msg: string) => void} [opts.log]
 */
export function createAdSizeSync({
  element,
  getAdsManager,
  viewMode,
  initialSize = null,
  observe,
  log,
}) {
  let applied = initialSize;
  let detach = null;

  /**
   * Push the current size to IMA if it changed and both sides are ready.
   *
   * @returns {{ width: number, height: number } | null} The size sent, else null.
   */
  function syncNow() {
    const size = readElementSize(element);
    if (!size) return null;

    const adsManager = getAdsManager();
    if (!adsManager || typeof adsManager.resize !== 'function') return null;

    if (applied && applied.width === size.width && applied.height === size.height) {
      return null;
    }

    adsManager.resize(size.width, size.height, viewMode);
    applied = size;
    log?.(`ad UI resized to ${size.width}x${size.height}`);
    return size;
  }

  function start() {
    if (detach || !element || typeof observe !== 'function') return;
    detach = observe(element, syncNow);
  }

  function stop() {
    detach?.();
    detach = null;
  }

  return { syncNow, start, stop };
}

/**
 * Default `observe`: ResizeObserver, falling back to window resize events on
 * engines that lack it (older Tizen web runtimes ship no ResizeObserver).
 *
 * @param {typeof globalThis} [scope]
 * @returns {(element: Element, onChange: () => void) => () => void}
 */
export function browserObserver(scope = globalThis) {
  return (element, onChange) => {
    if (typeof scope.ResizeObserver === 'function') {
      const observer = new scope.ResizeObserver(() => onChange());
      observer.observe(element);
      return () => observer.disconnect();
    }
    scope.addEventListener('resize', onChange);
    return () => scope.removeEventListener('resize', onChange);
  };
}
